import React, { useEffect, useState } from "react";
import { useAppState } from "../state/AppStateContext";
import { t } from "../i18n/strings";

function isStandalone() {
  return window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true;
}

function isIos() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

export default function InstallModal() {
  const { installOpen, closeInstall, lang } = useAppState();
  const [installPrompt, setInstallPrompt] = useState(null);
  const [installed, setInstalled] = useState(isStandalone);

  useEffect(() => {
    function capturePrompt(event) {
      event.preventDefault();
      setInstallPrompt(event);
    }
    function markInstalled() {
      setInstalled(true);
      setInstallPrompt(null);
      closeInstall();
    }
    window.addEventListener("beforeinstallprompt", capturePrompt);
    window.addEventListener("appinstalled", markInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", capturePrompt);
      window.removeEventListener("appinstalled", markInstalled);
    };
  }, [closeInstall]);

  if (!installOpen || installed) return null;
  const ios = isIos();

  async function install() {
    if (!installPrompt) return;
    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    if (choice?.outcome === "accepted") closeInstall();
    setInstallPrompt(null);
  }

  return (
    <div style={styles.backdrop} role="presentation">
      <div style={styles.dialog} role="dialog" aria-modal="true" aria-labelledby="install-title">
        <button type="button" onClick={closeInstall} aria-label={t(lang, "close")} style={styles.close}>×</button>
        <img src={`${process.env.PUBLIC_URL}/logo192.png`} alt="" aria-hidden="true" style={styles.icon} />
        <h2 id="install-title" style={styles.title}>{t(lang, "installTitle")}</h2>
        <p style={styles.body}>{t(lang, "installBody")}</p>
        {installPrompt ? (
          <button type="button" className="btn btn-primary btn-block" onClick={install} style={styles.primary}>
            {t(lang, "installButton")}
          </button>
        ) : ios ? (
          <ol style={styles.steps}>
            <li>{t(lang, "installIosShare")}</li>
            <li>{t(lang, "installIosHome")}</li>
            <li>{t(lang, "installIosAdd")}</li>
          </ol>
        ) : (
          <div style={styles.tip}>{t(lang, "installBrowserHelp")}</div>
        )}
        <button type="button" className="btn btn-block" onClick={closeInstall} style={styles.later}>
          {t(lang, "installLater")}
        </button>
      </div>
    </div>
  );
}

const styles = {
  backdrop: { position: "fixed", inset: 0, zIndex: 60, display: "grid", placeItems: "center", padding: 18, background: "rgba(4,12,20,.78)" },
  dialog: { position: "relative", width: "100%", maxWidth: 410, padding: "28px 24px 20px", borderRadius: 24, background: "var(--color-surface)", boxShadow: "var(--shadow-md)", textAlign: "center" },
  close: { position: "absolute", top: 12, right: 14, width: 38, height: 38, border: 0, borderRadius: "50%", background: "var(--panel)", color: "var(--color-text)", fontSize: 25, cursor: "pointer" },
  icon: { width: 84, height: 84, objectFit: "contain", filter: "drop-shadow(0 0 14px rgba(32,198,183,.3))" },
  title: { margin: "12px 0 8px", fontFamily: "var(--font-heading)", fontSize: 24 },
  body: { margin: "0 auto 20px", maxWidth: 310, color: "var(--color-neutral-700)", lineHeight: 1.5 },
  primary: { minHeight: 54, fontSize: 16 },
  steps: { margin: "4px 0 18px", padding: 0, listStylePosition: "inside", display: "grid", gap: 10, color: "var(--color-text)", lineHeight: 1.45, textAlign: "left" },
  tip: { padding: 16, borderRadius: 16, background: "var(--panel)", color: "var(--color-neutral-700)", lineHeight: 1.45 },
  later: { marginTop: 8, color: "var(--color-neutral-700)" },
};
